import cn from 'classnames';
import { INVISIBLE_ERROR } from 'components-machine';
import { isObject } from 'lodash';
import React, { createContext, CSSProperties, useContext, useMemo } from 'react';
import { RATING_TYPE, SchemaEngine } from 'schema';
import type { Question } from 'schema/poll';
import { WrapperProps } from 'src/entrypoints/root/wrapper';
import { RootContext } from '../../../../contexts/rootContext';
import { useResizeRoot } from '../../../../hooks/useResizeRoot';
import { inIframe } from '../../../../utils/location';
import { useControlsProps } from '../../../../utils/questionPropsHelper';
import { useRegisterTitleRef } from '../../../root/QuestionTitleContext';
import { ContentWrapper } from '../ContentWrapper';
import { Footer } from '../Footer';
import { Header } from '../Header';
import { RootWrapper } from '../RootWrapper';
import { MatrixHeader } from './MatrixHeader';
import { MatrixTitle } from './MatrixTitle';
import styles from './MatrixWrapper.module.css';
import { useMatrixViewMode } from './useMatrixViewMode';

const DEFAULT_WIDTH = 720;

export const MatrixContext = createContext<{ viewMode: MatrixViewModes; questionType: string }>({
  viewMode: 'default',
  questionType: '',
});

export const useMatrixContext = () => {
  return useContext(MatrixContext);
};

export type MatrixViewModes = 'default' | 'compact' | 'question';

export interface MatrixWrapperProps extends WrapperProps {
  firstQuestion: Question['data'];
  group: { id: string; text?: string; autoCommit?: boolean };
  deck?: { text?: string } | undefined;
  errorMessage?: string;
}

export const MatrixWrapper: React.FC<MatrixWrapperProps> = props => {
  const { schemaEngine } = useContext(RootContext);

  const {
    title,
    onBack,
    onClose,
    firstQuestion,
    progress,
    showBackButton,
    progressBar,
    isWebview,
    hasWelcome,
    children,
    group,
    deck,
    alignSurvey,
  } = props;

  const errorMessage = props.errorMessage && props.errorMessage !== INVISIBLE_ERROR ? props.errorMessage : '';

  const { viewMode, matrixRef } = useMatrixViewMode();

  const value = useMemo(() => ({ viewMode, questionType: firstQuestion.type }), [viewMode]);

  const matrixGrid = getMatrixGrid(schemaEngine, firstQuestion);
  const { gridStyle } = matrixGrid[viewMode];

  const controlProps = useControlsProps(props);

  const { registerTitleRef } = useRegisterTitleRef(group.id);

  useResizeRoot(matrixGrid.default.width, [matrixGrid.default.width]);

  return (
    <MatrixContext.Provider value={value}>
      <div className={cn(styles.container, { [c(styles.iframe)]: inIframe() })} ref={matrixRef}>
        <RootWrapper className={styles.container}>
          <Header
            className={styles.header}
            title={title}
            isWebview={isWebview}
            hasWelcome={hasWelcome}
            onBack={onBack}
            onClose={onClose}
            showBackButton={showBackButton}
            progress={{ ...progress, type: progressBar }}
            alignSurvey={alignSurvey}
          />

          <ContentWrapper flat={viewMode !== 'default'}>
            <div ref={registerTitleRef} className={cn(styles.matrixContainer, styles[viewMode])}>
              <MatrixTitle title={deck?.text} errorMessage={errorMessage} />

              <div className={styles.matrixGrid} style={gridStyle}>
                {viewMode === 'default' && <MatrixHeader question={firstQuestion} />}

                {children}
              </div>
            </div>
          </ContentWrapper>

          {controlProps.showFooter && <Footer className={styles.footer} {...controlProps} alignSurvey={alignSurvey} />}
        </RootWrapper>
      </div>
    </MatrixContext.Provider>
  );
};

const getStarWidth = (count: number) => {
  if (count < 6) {
    return 56;
  }

  if (count === 6) {
    return 50;
  }

  return 44;
};

const getDigitWidth = (count: number) => {
  if (count < 6) {
    return 56;
  }

  if (count < 8) {
    return 44;
  }

  if (count === 8) {
    return 40;
  }

  if (count === 9) {
    return 34;
  }

  return 32;
};

const getMatrixGrid = (
  engine: SchemaEngine,
  question: {
    type: Question['data']['type'];
    answers?: unknown[];
    minimumValue?: number;
    maximumValue?: number;
  },
): Record<MatrixViewModes, { width: number | null; gridStyle?: CSSProperties }> => {
  const spacing = 4;
  const baseWidth = 90;

  const defaultWidth = baseWidth + spacing * 2;
  const { type, answers = [], minimumValue, maximumValue } = question;

  let gridTemplateColumns = `auto repeat(${answers.length}, ${defaultWidth}px)`;
  let width = DEFAULT_WIDTH + (answers.length - 3) * defaultWidth - spacing;

  if (engine.hasTrait(type, RATING_TYPE) && maximumValue !== undefined) {
    const exclusiveCount = answers.filter(
      answer => isObject(answer) && 'exclusiveAnswer' in answer && !!answer.exclusiveAnswer,
    ).length;
    const firstExclusiveSpacing = 8;
    const firstExclusiveWidth = defaultWidth + firstExclusiveSpacing;

    let ratingCount = maximumValue - (minimumValue !== undefined ? minimumValue : 1) + 1;
    /** В матрицах со звездами есть только 1 поле: Размер шкалы */
    if (type === 'MATRIX_POLL_RATING_STARS') {
      ratingCount = maximumValue;
    }

    const ratingWidth = type === 'MATRIX_POLL_RATING_DIGITS' ? getDigitWidth(ratingCount) : getStarWidth(ratingCount);

    const exclusiveTemplateColumns = exclusiveCount
      ? exclusiveCount === 1
        ? `${firstExclusiveWidth}px`
        : `${firstExclusiveWidth}px repeat(${exclusiveCount - 1}, ${defaultWidth}px)`
      : '';

    gridTemplateColumns = `auto repeat(${ratingCount}, ${ratingWidth}px)` + exclusiveTemplateColumns;
    width =
      DEFAULT_WIDTH +
      (ratingCount - 5) * ratingWidth +
      exclusiveCount * defaultWidth +
      (exclusiveCount ? firstExclusiveSpacing : 0) -
      spacing;
  }

  return {
    question: { width: null, gridStyle: { gridTemplateColumns: `1fr` } },
    compact: { width: null },
    default: {
      width: Math.max(width, DEFAULT_WIDTH),
      gridStyle: {
        gridTemplateColumns,
        gridAutoRows: 'auto',
        gridRowGap: '4px',
        alignItems: 'center',
      },
    },
  };
};
